import { KeyboardLightingFeature } from '../../../../features/keyboard-lighting/contract'
import { LightingDomainRegistry } from '../../../../features/keyboard-lighting/LightingDomainRegistry'
import { staticDomain, dynamicDomain, sideDomain } from './domains'
import { ADDRESS_CH585M } from '../../protocols/address'
import { sendRead, sendWrite, checkACK } from '../../capabilities/memory'
import { CMD_CH585M, PROTOCOL_CONFIG } from '../../protocols/cmd'
import * as shared from '../../../shared/feature-impls/keyboard-lighting/lighting'
import type { LightingDomainType } from '../../../../types/lightingDomain'
import type { LightingConfigData } from '../../../../types/lightingDomain'
import type { KeyboardLightingTarget } from '../../../../types/lighting'
import type { keyboardLightingCustomConf } from '../../../../types/keyboardLighting'
import type { keyMapLighting } from '../../../../types/keyboardLighting'
import type { CommonAddress } from '../../../../types/protocol'

export class CH585MKeyboardLighting extends KeyboardLightingFeature {
  constructor(device: any) {
    super(device)
    LightingDomainRegistry.register(staticDomain)
    LightingDomainRegistry.register(dynamicDomain)
    LightingDomainRegistry.register(sideDomain)
  }

  getSupportedDomains() {
    return shared.getSupportedDomains()
  }

  async getEffects(target?: KeyboardLightingTarget) {
    return shared.getEffects(this.device, sendRead, target)
  }

  async getConfig(domain: LightingDomainType, index: number) {
    return shared.getConfig(this.device, sendRead, domain, index)
  }

  async setConfig(domain: LightingDomainType, index: number, config: Partial<LightingConfigData>) {
    return shared.setConfig(this.device, sendRead, sendWrite, domain, index, config)
  }

  async setProperty(
    domain: LightingDomainType,
    index: number,
    key: string,
    value: Parameters<typeof shared.setProperty>[6],
  ) {
    return shared.setProperty(this.device, sendRead, sendWrite, domain, index, key, value)
  }

  async getProperty(domain: LightingDomainType, index: number, key: string) {
    return shared.getProperty(this.device, sendRead, domain, index, key)
  }

  async setBacklightSwitch(enabled: boolean) {
    return shared.setBacklightSwitch(
      this.device,
      sendWrite,
      220,
      ADDRESS_CH585M.BACKLIGHT_SWITCH,
      enabled,
    )
  }

  async getBacklightSwitch() {
    return shared.getBacklightSwitch(this.device, sendRead, ADDRESS_CH585M.BACKLIGHT_SWITCH)
  }

  async setCurrentEffect(effect: number) {
    return shared.setCurrentEffect(
      this.device,
      sendWrite,
      220,
      ADDRESS_CH585M.BACKLIGHT_EFFECT,
      effect,
    )
  }

  async getCurrentEffect() {
    return shared.getCurrentEffect(this.device, sendRead, ADDRESS_CH585M.BACKLIGHT_EFFECT)
  }

  async setBrightness(brightness: number) {
    return shared.setBrightness(
      this.device,
      sendWrite,
      220,
      ADDRESS_CH585M.GLOBAL_BRIGHTNESS,
      brightness,
    )
  }

  async getBrightness() {
    return shared.getBrightness(this.device, sendRead, ADDRESS_CH585M.GLOBAL_BRIGHTNESS)
  }

  async setSpeed(speed: number) {
    return shared.setSpeed(this.device, sendWrite, 220, ADDRESS_CH585M.GLOBAL_SPEED, speed)
  }

  async getSpeed() {
    return shared.getSpeed(this.device, sendRead, ADDRESS_CH585M.GLOBAL_SPEED)
  }

  async setKeysColor(_domain: LightingDomainType, keyMap: keyMapLighting, index: number) {
    return shared.setKeysColor(
      this.device,
      sendWrite,
      220,
      22,
      ADDRESS_CH585M.STATIC_CUSTOM_LIGHT_RGB as unknown as Record<
        number,
        Record<number, Record<number, CommonAddress>>
      >,
      ADDRESS_CH585M.STATIC_LIGHT_R0C0,
      keyMap,
      index,
    )
  }

  async getKeysColor(domain: LightingDomainType, index: number) {
    return shared.getKeysColor(
      this.device,
      sendRead,
      domain,
      index,
      22,
      6,
      ADDRESS_CH585M.STATIC_CUSTOM_LIGHT_RGB as unknown as Record<
        number,
        Record<number, Record<number, CommonAddress>>
      >,
    )
  }

  async *getKeysColorStream(domain: LightingDomainType, index: number) {
    yield* shared.getKeysColorStream(
      this.device,
      sendRead,
      domain,
      index,
      22,
      6,
      ADDRESS_CH585M.STATIC_CUSTOM_LIGHT_RGB as unknown as Record<
        number,
        Record<number, Record<number, CommonAddress>>
      >,
    )
  }

  async createCustomEffect(config: keyboardLightingCustomConf, index: number) {
    return shared.createCustomEffect(
      this.device,
      sendWrite,
      0x14,
      0x0c,
      ADDRESS_CH585M.STATIC_LIGHT_EFFECT_ADDRESS_NAME_LEVEL,
      ADDRESS_CH585M.STATIC_LIGHT_TYPE,
      config,
      index,
    )
  }

  async deleteCustomEffect(index: number) {
    return shared.deleteCustomEffect(
      this.device,
      sendRead,
      sendWrite,
      checkACK,
      220,
      ADDRESS_CH585M.BACKLIGHT_EFFECT,
      [...CMD_CH585M.delete_Lighting],
      PROTOCOL_CONFIG.report_id,
      PROTOCOL_CONFIG.feature_report_id,
      index,
    )
  }

  async setSideEffect(effect: number) {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_CH585M.SIDE_LIGHT_ENABLE,
    )
    const reg = shared.parseAuxLightingEnableBytes(bytes)
    reg.sideLightMode = effect
    await shared.setSideLightEnableBytes(
      this.device,
      sendWrite,
      220,
      ADDRESS_CH585M.SIDE_LIGHT_ENABLE,
      Array.from(shared.serializeAuxLightingEnableRegister(reg)),
    )
  }

  async getSideEffect() {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_CH585M.SIDE_LIGHT_ENABLE,
    )
    return shared.parseAuxLightingEnableBytes(bytes).sideLightMode
  }

  async setSideLightDynamicEnable(enable: boolean) {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_CH585M.SIDE_LIGHT_ENABLE,
    )
    const reg = shared.parseAuxLightingEnableBytes(bytes)
    reg.sideLightEnabled = enable
    await shared.setSideLightEnableBytes(
      this.device,
      sendWrite,
      220,
      ADDRESS_CH585M.SIDE_LIGHT_ENABLE,
      Array.from(shared.serializeAuxLightingEnableRegister(reg)),
    )
  }

  async getSideLightDynamicEnable() {
    const bytes = await shared.getSideLightEnableBytes(
      this.device,
      sendRead,
      ADDRESS_CH585M.SIDE_LIGHT_ENABLE,
    )
    return shared.parseAuxLightingEnableBytes(bytes).sideLightEnabled
  }
}
